import { useState, type DragEvent } from "react";

/**
 * Dragging an item onto a classeur — a tile, a group header, the bar shown while dragging. Targets are
 * strings the page chooses: a classeur id, and its own key for « Sans classeur ».
 */
export function useFolderDrop<T extends { id: string }>({
  canDrop,
  onDrop,
}: {
  /** Whether `item` may be dropped on `target`: never on the classeur it already sits in. */
  canDrop: (item: T, target: string) => boolean;
  onDrop: (item: T, target: string) => void;
}) {
  const [dragged, setDragged] = useState<T | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const canDropOn = (target: string) =>
    dragged !== null && canDrop(dragged, target);
  const endDrag = () => {
    setDragged(null);
    setDropTarget(null);
  };

  /** Starts dragging `item`; spread on a draggable element, or call from its own handler. */
  const startDrag = (item: T, event: DragEvent<HTMLElement>) => {
    event.dataTransfer.effectAllowed = "move";
    // Firefox starts no drag without data.
    event.dataTransfer.setData("text/plain", item.id);
    // After the browser has taken the drag image, so the item is not dimmed in it.
    window.setTimeout(() => setDragged(item), 0);
  };

  const dropZone = (target: string) => ({
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!canDropOn(target)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (dropTarget !== target) setDropTarget(target);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }
      setDropTarget((current) => (current === target ? null : current));
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      const item = dragged;
      endDrag();
      if (item && canDrop(item, target)) onDrop(item, target);
    },
  });

  /** The border a drop zone takes: highlighted under the pointer, dashed while it would accept. */
  const dropStyle = (target: string) =>
    dropTarget === target
      ? " border-primary bg-primary/10"
      : canDropOn(target)
        ? " border-dashed border-primary/50"
        : "";

  return { dragged, canDropOn, startDrag, endDrag, dropZone, dropStyle };
}

export type FolderDrop = ReturnType<typeof useFolderDrop>;
